// @ts-strict-ignore
import { capitalize, isEmpty, startCase, upperCase } from 'lodash-es'
import { useGetInfoRequired } from 'hooks/queries/useGetInfoRequiredQuery'
import { EMBEDDED_CUSTOMER_STATUSES } from '../helpers/constants'
import {
  AssociateRequirement,
  EmbeddedCustomerAssociate,
  InfoRequired,
  UnifiedAssociate,
} from '../helpers/types'

export const useAssociateDetails = (
  reference: string,
  associatesData?: EmbeddedCustomerAssociate[]
) => {
  const { data, isLoading, isError, refetch } = useGetInfoRequired(reference)
  const infoRequired: InfoRequired['infoRequired'] = data?.infoRequired
  const status: InfoRequired['status'] = data?.status
  const pendingAssociatesRaw: AssociateRequirement[] = infoRequired?.associates || []

  const mapVerifiedAssociates = (verified?: EmbeddedCustomerAssociate[]): UnifiedAssociate[] => {
    if (!verified?.length) return []
    return verified
      .filter(v => {
        return !pendingAssociatesRaw.some(
          p => p?.customerPersonReference && p.customerPersonReference === v?.person?.reference
        )
      })
      .map(v => ({
        id: `${v?.person?.firstName ?? ''}-${v?.person?.lastName ?? ''}`.trim(),
        title: capitalize(upperCase(v?.title ?? 'Associate')),
        body: `${v?.person?.firstName ?? ''} ${v?.person?.lastName ?? ''}`.trim(),
        status: EMBEDDED_CUSTOMER_STATUSES.VERIFIED,
      }))
  }

  const mapPendingIdentificationUploadAssociates = (
    pending: AssociateRequirement[]
  ): UnifiedAssociate[] => {
    if (!pending?.length) return []
    return pending
      .filter(p => p?.associateName)
      .map(p => {
        const roles: string[] = Array.isArray(p?.roles) ? p.roles : []
        const rolesTitle = roles.length
          ? roles.map(r => startCase(r.toLowerCase())).join(' / ')
          : startCase('associate')
        return {
          id: `${p?.associateName ?? ''}-${roles[0] ?? 'PENDING'}`.trim(),
          title: rolesTitle,
          body: String(p?.associateName ?? ''),
          status: EMBEDDED_CUSTOMER_STATUSES.PENDING,
          documents: p?.documents ?? [],
        }
      })
  }

  const pendingVerificationStatus =
    isEmpty(infoRequired) &&
    status !== EMBEDDED_CUSTOMER_STATUSES.VERIFIED &&
    infoRequired?.associates?.length

  const unifiedAssociates: UnifiedAssociate[] = [
    ...mapPendingIdentificationUploadAssociates(pendingAssociatesRaw),
    ...mapVerifiedAssociates(associatesData),
  ]

  const requiresAssociates = !!infoRequired?.associates?.find(a => a.roles)?.roles?.length

  return {
    associates: unifiedAssociates,
    isLoading,
    isError,
    refetch,
    pendingVerificationStatus,
    requiresAssociates,
  }
}
